import * as React from 'react';

import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';

import { cn } from '../../lib';
import { ThemeColor } from '../../types';
import {
  Button,
  ButtonProps,
  Col,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
} from '../ui';

interface ConfirmPopoverButtonProps {
  children?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  title?: string;
  color?: ThemeColor;
  buttonProps?: ButtonProps;
  isLoading?: boolean;
  renderTrigger?: (
    props: ButtonProps & { isLoading: boolean },
  ) => React.ReactNode;
  placement?: AriaPopoverProps['placement'];
  className?: string;
  contentClassName?: string;
}

//
// ⚛️ ⚛️ ⚛️ CONFIRM POPOVER BUTTON ⚛️ ⚛️ ⚛️
//
export const ConfirmPopoverButton: React.FC<ConfirmPopoverButtonProps> = ({
  children,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  color = 'failure',
  buttonProps,
  isLoading = false,
  renderTrigger,
  placement,
  className,
  contentClassName,
}) => {
  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [isPending, startTransition] = React.useTransition();

  // Callbacks
  const handleConfirm = React.useCallback(() => {
    startTransition(async () => {
      await onConfirm();
      setIsOpen(false);
    });
  }, [onConfirm]);

  const handleDiscard = React.useCallback(() => setIsOpen(false), []);

  const triggerElement = renderTrigger ? (
    renderTrigger({ ...buttonProps, isLoading: isPending || isLoading })
  ) : (
    <Button {...buttonProps}>{children}</Button>
  );

  return (
    <div className={cn('@ConfirmPopoverButton', className)}>
      <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        {triggerElement}
        <Popover placement={placement}>
          <PopoverDialog>
            <Col gap4 className={cn('min-w-[220px] p-4', contentClassName)}>
              {title && (
                <div className="text-sm font-semibold text-neutral">
                  {title}
                </div>
              )}
              <div className="text-sm text-neutral/80">{message}</div>
              <Row xEnd gap2>
                <Button
                  variant="outline"
                  color="neutral"
                  onPress={handleDiscard}
                  isLoading={isPending || isLoading}
                >
                  {cancelLabel}
                </Button>

                <Button
                  variant="solid"
                  color={color}
                  onPress={handleConfirm}
                  isLoading={isPending || isLoading}
                >
                  {confirmLabel}
                </Button>
              </Row>
            </Col>
          </PopoverDialog>
        </Popover>
      </PopoverTrigger>
    </div>
  );
};
